import { useState } from "react";
import { cn } from "../util";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

// Default emoji set - can be extended
const DEFAULT_EMOJIS = ["👍", "❤️", "😂", "😮", "🎉"] as const;

export interface MessageReaction {
  emoji: string;
  participantId: string;
  messageId: string;
}

export interface MessageReactionsProps {
  messageId: string;
  participantId: string;
  onReact: (emoji: string) => void;
  onUnreact: (emoji: string) => void;
  reactions?: MessageReaction[];
  emojis?: string[];
  displayMode?: "collapsed" | "expanded";
  showCount?: boolean;
  className?: string;
}

export const MessageReactions = ({
  messageId,
  participantId,
  onReact,
  onUnreact,
  reactions = [],
  emojis = [...DEFAULT_EMOJIS],
  displayMode = "collapsed",
  showCount = true,
  className,
}: MessageReactionsProps) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Aggregate reactions by emoji
  const reactionCounts = reactions.reduce(
    (acc, reaction) => {
      if (reaction.messageId === messageId) {
        if (!acc[reaction.emoji]) {
          acc[reaction.emoji] = {
            count: 0,
            participantIds: [],
          };
        }
        acc[reaction.emoji].count++;
        acc[reaction.emoji].participantIds.push(reaction.participantId);
      }
      return acc;
    },
    {} as Record<string, { count: number; participantIds: string[] }>,
  );

  // Get count and participant IDs for an emoji
  const getEmojiData = (emoji: string) => {
    return reactionCounts[emoji] ?? { count: 0, participantIds: [] };
  };

  // Check if current participant has reacted with this emoji
  const hasReacted = (emoji: string) => {
    return getEmojiData(emoji).participantIds.includes(participantId);
  };

  // Handle reaction toggle
  const handleReactionClick = (emoji: string) => {
    if (hasReacted(emoji)) {
      onUnreact(emoji);
    } else {
      onReact(emoji);
    }
    setIsPickerOpen(false);
  };

  // Render an emoji button with count
  const renderEmojiButton = (emoji: string) => {
    const { count, participantIds } = getEmojiData(emoji);
    const isReacted = hasReacted(emoji);
    const hasZeroCount = count === 0;

    return (
      <TooltipProvider key={emoji}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => handleReactionClick(emoji)}
              className={cn(
                "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs",
                "border border-transparent bg-background",
                "hover:border-foreground hover:bg-secondary transition-colors",
                isReacted && "bg-primary/10",
                hasZeroCount && "opacity-75"
              )}
            >
              <span>{emoji}</span>
              {showCount && <span className="text-muted-foreground">{count}</span>}
            </button>
          </TooltipTrigger>
          {/* <TooltipContent>
            <p className="text-xs">
              {count === 0
                ? "No reactions"
                : count === 1
                  ? "1 reaction"
                  : `${count} reactions`}
            </p>
          </TooltipContent> */}
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (displayMode === "expanded") {
    // Expanded mode: show all emojis with counts, no + button
    return (
      <div className={cn("flex items-center gap-1 flex-wrap", className)}>
        {emojis.map((emoji) => renderEmojiButton(emoji))}
      </div>
    );
  }

  // Collapsed mode: show only reacted emojis + picker button
  return (
    <div className={cn("flex items-center gap-1 flex-wrap", className)}>
      {/* Only show emojis that have reactions */}
      {Object.entries(reactionCounts).map(([emoji, { count, participantIds: _participantIds }]) => (
        <TooltipProvider key={emoji}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => handleReactionClick(emoji)}
                className={cn(
                  "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs",
                  "border border-transparent bg-background",
                  "hover:border-foreground hover:bg-secondary transition-colors",
                  hasReacted(emoji) && "bg-primary/10"
                )}
              >
                <span>{emoji}</span>
                {showCount && <span className="text-muted-foreground">{count}</span>}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">
                {count === 1 ? "1 reaction" : `${count} reactions`}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}

      {/* Add reaction button */}
      <div className="relative">
        <button
          onClick={() => setIsPickerOpen(!isPickerOpen)}
          className={cn(
            "inline-flex items-center justify-center w-6 h-6 rounded text-xs",
            "border border-dashed border-border bg-background",
            "hover:bg-secondary hover:border-solid transition-colors",
            "text-muted-foreground hover:text-foreground"
          )}
          aria-label="Add reaction"
        >
          +
        </button>

        {/* Emoji picker dropdown */}
        {isPickerOpen && (
          <>
            {/* Backdrop to close picker */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsPickerOpen(false)}
            />

            {/* Picker */}
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 z-20 p-1 rounded-md border border-border bg-background shadow-lg flex gap-0.5">
              {emojis.map((emoji) => {
                const { count } = getEmojiData(emoji);
                return (
                  <button
                    key={emoji}
                    onClick={() => handleReactionClick(emoji)}
                    className={cn(
                      "w-8 h-8 rounded hover:bg-secondary transition-colors",
                      "flex flex-col items-center justify-center text-xs",
                      hasReacted(emoji) && "bg-primary/10"
                    )}
                    aria-label={`React with ${emoji}`}
                  >
                    <span>{emoji}</span>
                    {count > 0 && (
                      <span className="text-[10px] text-muted-foreground leading-none">
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
